import {
  AppstoreAddOutlined,
  ArrowDownOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  CommentOutlined,
  CopyOutlined,
  DeleteOutlined,
  DislikeOutlined,
  DownOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LikeOutlined,
  MessageOutlined,
  OpenAIFilled,
  PaperClipOutlined,
  PlusOutlined,
  ProductOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import {
  Attachments,
  type AttachmentsProps,
  Bubble,
  BubbleProps,
  Conversations,
  Prompts,
  Sender,
  Suggestion,
  Welcome,
  useXAgent,
  useXChat,
} from '@ant-design/x';
import type { Conversation } from '@ant-design/x/es/conversations';
import { Avatar, Button, FloatButton, GetProp, GetRef, Image, Popover, Space, Spin, Tooltip, message as antMessage } from 'antd';
import { createStyles } from 'antd-style';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { chatService } from '../../services/chatService';
import { useCopilotStyle } from './useCopilotStyle';
import { ChatSession } from '@src/services/models';
import { StableMarkdownRenderer } from '../content/smartContent';
import { 
  getSessions, getSessionMessages, createSession, 
  deleteSession
} from "../../axios/api.chat";
type BubbleDataType = {
  role: string;
  content: string;
};

const MOCK_SUGGESTIONS = [
  { label: 'Write a report', value: 'report' },
  { label: 'Draw a picture', value: 'draw' },
  {
    label: 'Check some knowledge',
    value: 'knowledge',
    icon: <OpenAIFilled />,
    children: [
      { label: 'About React', value: 'react' },
      { label: 'About Ant Design', value: 'antd' },
    ],
  },
];
const MOCK_QUESTIONS = [
  '什么是 APNs，怎么配置？',
  '如何进行ABM配置？',
  '什么是配置文件，如何添加？',
];

const AGENT_PLACEHOLDER = 'Generating content, please wait...';

interface CopilotProps {
  copilotOpen: boolean;
  setCopilotOpen: (open: boolean) => void;
  onFullscreenChange?: (fullscreen: boolean) => void;  // 新增回调
}

const Copilot = (props: CopilotProps) => {
  const { copilotOpen, setCopilotOpen, onFullscreenChange } = props;
  const { styles } = useCopilotStyle();
  const attachmentsRef = useRef<GetRef<typeof Attachments>>(null);
  const abortController = useRef<AbortController | null>(null);
  // ==================== State ====================

  const [messageHistory, setMessageHistory] = useState<Record<string, any>>({});

  const [sessionList, setSessionList] = useState<ChatSession[]>([]);
  const [curSession, setCurSession] = useState<string | null>(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [files, setFiles] = useState<GetProp<AttachmentsProps, 'items'>>([]);

  const [inputValue, setInputValue] = useState('');
  const [lastUserMessage, setLastUserMessage] = useState<string>('');
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const latestContentRef = useRef(''); // 👈 新增：用于跨回调共享状态
  const inputRef = useRef<any>(null);

  const chatListRef = useRef<HTMLDivElement>(null);
  const debouncedScroll = useRef<NodeJS.Timeout | null>(null);
  const debouncedUpdateRef = useRef<NodeJS.Timeout | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const lastScrollCheckRef = useRef(0);
  const SCROLL_CHECK_THROTTLE = 200; // 200ms 内只检查一次
  /**
   * 🔔 Please replace the BASE_URL, PATH, MODEL, API_KEY with your own values.
   */

  // ==================== Runtime ====================
  const SHOW_THRESHOLD = 100; // 向下滚动超过 100px 才显示按钮
  const HIDE_THRESHOLD = 50;  // 向上滚动到 50px 以内才隐藏按钮
  const token = localStorage.getItem('jt');
  const BASE_URL = `${process.env.REACT_APP_API_URL}/api/v1/chat/completions`;// 'https://localhost:5001/api/v1/chat/completions'
  
  const [agent] = useXAgent<BubbleDataType>({
    baseURL: BASE_URL,
    model: "q1q",
    dangerouslyApiKey: `Bearer ${token}`,
  });

  const toggleFullscreen = () => {
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);
    onFullscreenChange?.(newFullscreenState);  // 通知父组件
  };

  const getDistanceFromBottom = useCallback((container: HTMLElement): number => {
    return container.scrollHeight - container.scrollTop - container.clientHeight;
  }, []);

  const updateScrollButtonState = useCallback(() => {
    const container = document.getElementById('bubble-list-container');
    if (!container) return;

    const distance = getDistanceFromBottom(container);

    setShowScrollToBottom(prev => {
      if (prev && distance <= HIDE_THRESHOLD) {
        return false; // 从显示 → 隐藏
      }
      if (!prev && distance > SHOW_THRESHOLD) {
        return true;  // 从隐藏 → 显示
      }
      return prev; // 中间区域保持原状态（防抖关键！）
    });
  }, [getDistanceFromBottom]);

  //const loading = agent.isRequesting();
  const { messages, onRequest, setMessages } = useXChat({
    agent,
    requestFallback: (_, { error }) => {
      if (error.name === 'AbortError') {
        return {
          content: latestContentRef.current || '（回复已被中断）',
          //content: 'Request is aborted',
          role: 'assistant',
          //status: 'aborted'
        };
      }
      else if(error.message.includes('401'))
      {
        return {
          content: '登录已超时，请 [点击重新登录](/login)',
          role: 'assistant',
        };
      }
      return {
        content: 'Request failed, please try again!',
        role: 'assistant',
      };
    },
    transformMessage: (info) => {
      const { originMessage, chunk } = info || {};

      const rawData = chunk?.data;
      if (rawData == null) {
        return originMessage || { content: '', role: 'assistant' };
      }
       const data = rawData.trim();
      // 处理 [DONE] 标记
      if (data === '[DONE]') {
        const finalContent = originMessage?.content || '';
        latestContentRef.current = finalContent; // 更新 ref
        // 清除之前的定时器
        if (debouncedScroll.current) clearTimeout(debouncedScroll.current);
        // 延迟 30ms 滚动，避免频繁调用
        debouncedScroll.current = setTimeout(() => {
          scrollToBottom(false);
        }, 30);

        return {
          content: finalContent,
          role: 'assistant',
        };
      }

      let currentContent = '';    
      try {
        if (data && data !== '[DONE]') {
          const message = JSON.parse(data);
          
          const delta = message?.choices?.[0]?.delta;
          
          // 处理 content 为 null 的情况
          if (delta?.content === null) {
            currentContent = ''; // 将 null 转换为空字符串
          } else if (delta?.content !== undefined) {
            currentContent = delta.content || '';
          }
        }
      } catch (error) {
        console.warn('解析数据块失败:', error);
      }

      const content = (originMessage?.content || '') + currentContent;
      latestContentRef.current = content; // 👈 关键：实时更新 ref

      if (debouncedScroll.current) clearTimeout(debouncedScroll.current);
      debouncedScroll.current = setTimeout(() => {
        scrollToBottom(false);
      }, 30);

      return {
        content: content,
        role: 'assistant',
      };
    },
    resolveAbortController: (controller) => {
      abortController.current = controller;
      latestContentRef.current = '';
    },
  });

  const isScrolledToBottom = useCallback((container: HTMLElement): boolean => {
    return getDistanceFromBottom(container) <= HIDE_THRESHOLD;
  }, [getDistanceFromBottom]);

  const scrollToBottom = useCallback((force = false, behavior: ScrollBehavior = 'smooth') => {
    // 选中 Bubble.List 的内部滚动容器
    const container = document.getElementById('bubble-list-container');
    if (!container) return;

    if (force) {
      setTimeout(() => {container.scrollTo({ top: container.scrollHeight, behavior })},300);
      setShowScrollToBottom(false); // ✅ 用户主动行为，立即隐藏是合理的
      return;
    }

    if (isScrolledToBottom(container)) {
      container.scrollTo({ top: container.scrollHeight, behavior });
      // 因为可能是自动触发（如图片加载），应交由 scroll 事件判断
      setTimeout(updateScrollButtonState, 100);
    }
  }, [isScrolledToBottom, updateScrollButtonState]);

  const loading = messages.some(
    (msg) => msg.status === 'loading' || msg.status === 'updating'
  );

  useEffect(() => {
    const container = document.getElementById('bubble-list-container');
    if (!container) return;

    const handleImageLoad = () => {
      // 图片加载完成，尝试补偿滚动
      setTimeout(() => {
        // 关键：这里不要用 scrollToBottom(false)!
        // 而是判断“是否接近底部”，如果是，就滚到底
        const distance = getDistanceFromBottom(container);
        if (distance <= 150) { // 比如 150px 内都认为“应该跟”
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'auto', // 避免 smooth 干扰
          });
        }
        setTimeout(updateScrollButtonState, 100); // 同步按钮状态
      }, 50); // 等待布局稳定
    };
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          const el = node as HTMLElement;
          const imgs = [
            ...(el.tagName === 'IMG' ? [el] : []),
            ...Array.from(el.querySelectorAll?.('img') || []),
          ] as HTMLImageElement[];

          imgs.forEach((img) => {
            // 已加载？立即处理
            if (img.complete && img.naturalHeight > 0) {
              handleImageLoad();
            } else {
              // 监听 load/error
              img.addEventListener('load', handleImageLoad, { once: true });
              img.addEventListener('error', handleImageLoad, { once: true });
            }

            // 👇 额外监听 src 变化（防 Markdown 延迟赋值）
            const srcObserver = new MutationObserver(() => {
              if (img.src && !img.complete) {
                img.addEventListener('load', handleImageLoad, { once: true });
                img.addEventListener('error', handleImageLoad, { once: true });
              }
            });
            srcObserver.observe(img, { attributes: true, attributeFilter: ['src'] });
          });
        });
      });
    });

    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [scrollToBottom]);

  // 滚动监听
  useEffect(() => {
    const container = document.getElementById('bubble-list-container');
    if (!container) return;

    const handleScroll = () => {
      updateScrollButtonState();
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // 初始化状态

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [updateScrollButtonState]);

  useEffect(() => {
    return () => {
      if (debouncedUpdateRef.current) {
        clearTimeout(debouncedUpdateRef.current);
      }
      if (debouncedScroll.current) {
        clearTimeout(debouncedScroll.current);
      }
    };
  }, []);

  // 在组件中添加调试
  // useEffect(() => {
  //   console.log('=== 请求状态调试 ===');
  //   console.log('agent.isRequesting():', agent.isRequesting());
  //   console.log('loading state:', loading);
  //   console.log('messages count:', messages.length);
    
  //   if (messages.length > 0) {
  //     const lastMessage = messages[messages.length - 1];
  //     console.log('最后一条消息:', {
  //       status: lastMessage.status,
  //       role: lastMessage.message.role,
  //       content: lastMessage.message.content
  //     });
  //   }
  // }, [loading, messages, agent]);


  // ==================== Event ====================
  const handleUserSubmit = (val: string) => {
    setLastUserMessage(val);
    scrollToBottom(true);
    if (!curSession) {
      createNewSession(val).then((res) => {
        var sessionId = res?.id;
        onRequest({
          stream: true,
          message: { content: val, role: 'user' },
          sessionId: sessionId,
          new_session: !sessionId,
          options: {
            signal: abortController.current?.signal, // 确保信号传递
          },
        });
      });
      return;
    }
    onRequest({
      stream: true,
      message: { content: val, role: 'user' },
      sessionId: curSession,
      new_session: !curSession,
      options: {
        signal: abortController.current?.signal, // 确保信号传递
      },
    });
  };

  // 会话管理函数
  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      //const response = await chatService.getSessions();
      const response = await getSessions();
      const sessions = response.sessions || [];
      setSessionList(sessions);
      
      if (!curSession && response.sessions.length > 0) {
        const firstSession = response.sessions[0];
        setCurSession(firstSession.id);
        await loadSessionMessages(firstSession.id);
      }
    } catch (error) {
      console.log(error);
      antMessage.error('加载会话列表失败');
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSessionMessages = async (sessionId: string) => {
    if (!sessionId) return;
    
    try {
      setLoadingMessages(true);
      //const sessionMessages = await chatService.getSessionMessages(sessionId);
      const response = await getSessionMessages(sessionId);

      var sessionMessages = response.messages;
      const formattedMessages = sessionMessages.map((msg: any, index: any) => ({
        id: `msg-${sessionId}-${index}`,
        message: { role: msg.role, content: msg.content },
        status: 'success' as const,
      }));
      setMessages(formattedMessages);
      scrollToBottom(true);
      //setMessages([...messages,s]);
    } catch (error) {
      console.error('加载会话消息失败:', error);
      throw error;
    } finally {
      setLoadingMessages(false);
    }
  };

  const generateTitleFromQuestion = (question: string) => {
    if (!question) return '新会话';
    
    // 取前15个字符（按字符数，不是字节数）
    const trimmed = question.trim();
    if (trimmed.length <= 15) {
      return trimmed || '新会话';
    }
    
    return trimmed.substring(0, 15) + '...';
  }

  const createNewSession = async (content: string) => {
    if (agent.isRequesting()) {
      antMessage.error(
        'Message is Requesting, you can create a new conversation after request done or abort it right now...',
      );
      return;
    }
      setMessages([]);
      var title = generateTitleFromQuestion(content);
    //if (messages?.length) {
      // The abort execution will trigger an asynchronous requestFallback, which may lead to timing issues.
      // In future versions, the sessionId capability will be added to resolve this problem.
      //const newSession = await chatService.createSession(title);
      const newSession = await createSession({title});
      setSessionList(prev => [newSession, ...prev]);
      setCurSession(newSession.id);
      return newSession;
  };

  const switchSession = async (sessionId: string) => {
    if (loading) {
      antMessage.error('请等待当前请求完成');
      return;
    }
    if(sessionId == curSession)
      return;

    try {
      abortController.current?.abort();
      await loadSessionMessages(sessionId);
      setCurSession(sessionId);
      antMessage.success('已切换会话');
    } catch (error) {
      antMessage.error('切换会话失败');
    }
    finally{
      setPopoverVisible(false);
    }
  };

  // const deleteSession = async (sessionId: string) => {
  //   try {
  //     //await chatService.deleteSession(sessionId);
  //     await deleteSession(sessionId);
  //     setSessionList(prev => prev.filter(s => s.id !== sessionId));
      
  //     if (curSession === sessionId) {
  //       const otherSessions = sessionList.filter(s => s.id !== sessionId);
  //       if (otherSessions.length > 0) {
  //         setCurSession(otherSessions[0].id);
  //       } else {
  //         await createNewSession('');
  //       }
  //     }
      
  //     antMessage.success('会话已删除');
  //   } catch (error) {
  //     antMessage.error('删除会话失败');
  //   }
  // };

  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      antMessage.success('已复制到剪贴板');
    } catch (err) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        antMessage.success('已复制到剪贴板');
      } catch (fallbackErr) {
        antMessage.error('复制失败');
      }
      document.body.removeChild(textArea);
    }
  };

  const regenerateResponse = () => {
    if (!curSession) {
      antMessage.error('没有活跃的会话');
      return;
    }

    if (messages.length < 2) {
      antMessage.warning('没有可以重新生成的消息');
      return;
    }

    // 确保最后两条消息是 用户 -> 助理 的顺序
    const lastMessage = messages[messages.length - 1];
    const secondLastMessage = messages[messages.length - 2];

    if (lastMessage.message.role !== 'assistant' || secondLastMessage.message.role !== 'user') {
      antMessage.warning('消息格式不符合重新生成要求');
      return;
    }

    const userMessageContent = secondLastMessage.message.content;
    const userMessageId = secondLastMessage.id;
    const assistantMessageId = lastMessage.id;
    console.log(userMessageId,assistantMessageId);
    // 调用后端API删除这两条消息
    // if (curSession && userMessageId && assistantMessageId) {
    //   chatService.deleteSessionMessages(curSession, [userMessageId, assistantMessageId])
    //     .catch(error => {
    //       console.error('删除后端消息失败:', error);
    //     });
    // }

    // 更新前端状态：删除最后两条消息（用户消息和助理消息）
    const newMessages = messages.slice(0, -2);
    setMessages(newMessages);

    // 重新发送用户消息
    setTimeout(() => {
      onRequest({
        stream: true,
        message: { content: userMessageContent, role: 'user' },      
        sessionId: curSession,
        new_session: !curSession,
        options: {
          signal: abortController.current?.signal, // 确保信号传递
        },
        isRetry: true
      });
    }, 100);
  };

  const renderMarkdown: BubbleProps['messageRender'] = (content) => {
    return (
      <StableMarkdownRenderer content={content}></StableMarkdownRenderer>
    );
  };
  // ==================== Nodes ====================

  const chatSider = (
    <div className={styles.sider}>
      {/* 🌟 Logo */}
      <div className={styles.logo}>
        <img
          src="/images/ai-logo.png"
          draggable={false}
          alt="logo"
          width={30}
          height={30}
        />
        <span>AI助理</span>
      </div>

      {/* 添加会话 */}
      <Button
        onClick={() => {createNewSession('');}}
        type="link"
        className={styles.addBtn}
        icon={<PlusOutlined />}
        disabled={messages.length == 0}
      >
        新建会话
      </Button>

      {/* 会话管理 */}
      <Conversations
        className={styles.newConversations}
        items={sessionList.map((session) => ({
          key: session.id,
          label: session.id === curSession ? `${session.title}` : session.title,
          group: dayjs(session.updatedAt).format('YYYY-MM-DD'),
        }))}
        activeKey={curSession || undefined}
        onActiveChange={switchSession}
        groupable
        styles={{ item: { padding: '0 8px' } }}
        // menu={(conversation) => ({
        //   items: [
        //     {
        //       label: 'Rename',
        //       key: 'rename',
        //       icon: <EditOutlined />,
        //     }
        //   ],
        // })}
      />
    </div>
  );
  
  const chatHeader = (
    <div className={styles.chatHeader} style={{ borderBottom: !isFullscreen ? '1px solid #d9d9d9':'' }}>
      <div className={styles.headerTitle}>
        {!isFullscreen && <>
          <img style={{verticalAlign: 'middle', paddingTop:'2px'}} width={30} src="/images/ai-logo.png" />
          <span style={{ verticalAlign: 'middle', paddingTop:'2px', marginLeft: '8px' }}>AI助理</span>
        </>
        }
      </div>
      <Space size={0}>
        {!isFullscreen && <Tooltip title={"新建会话"}><Button
          type="text"
          icon={<PlusOutlined />}
          onClick={() => {createNewSession('')}}
          className={styles.headerButton}
          disabled={messages.length == 0}
        /></Tooltip>}
        {!isFullscreen && <Popover
          open={popoverVisible}
          onOpenChange={setPopoverVisible}
          placement="bottom"
          styles={{ body: { padding: 0, maxHeight: 600,overflow: 'auto' } }}
          content={loadingSessions ? 
              (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <Spin size="small" />
                </div>
              ) : 
              (<div  style={{  overflow: 'auto' }}>
                <Conversations
                  items={sessionList.map((session) => ({
                    key: session.id,
                    label: session.id === curSession ? `[当前] ${session.title}` : session.title,
                    group: dayjs(session.updatedAt).format('YYYY-MM-DD'),
                  }))}
                  activeKey={curSession || undefined}
                  groupable
                  onActiveChange={switchSession}
                  styles={{ item: { padding: '0 8px' } }}
                  className={styles.conversations}
                />
              </div>
              )

          }
        >
          <Button type="text" icon={<CommentOutlined />} className={styles.headerButton} />
        </Popover>}
        <Tooltip key={isFullscreen ? 'exit-fs' : 'enter-fs'} title={isFullscreen ? "退出全屏" : "全屏模式"}>
          <Button
            type="text"
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleFullscreen}
            className={styles.headerButton}
          />
        </Tooltip>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => setCopilotOpen(false)}
          className={styles.headerButton}
        />
      </Space>
    </div>
  );
  const chatList = (
    <div
      //ref={chatListRef}
      className={styles.chatList}
      style={{
        height: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        paddingInline: 16, // 统一控制左右留白
        margin: isFullscreen ? '0 15%':'0 0'
      }}
    >
      {/* 始终渲染 Bubble.List —— 这是关键！ */}
      <Bubble.List
        id="bubble-list-container"
        // autoScroll
        // onScroll={(e) => {
        //   //updateScrollButtonState();
        //   scheduleUpdateScrollButtonState();
        // }}
        style={{
          height: '100%',
          //paddingInline: 16,
          overflow: 'auto',
          display: !loadingMessages && (!messages || messages.length === 0) ? 'none' : 'block', // 当无消息或加载中时不显示
        }}

        items={
          loadingMessages
            ? [] // 加载中时不显示任何消息项（但容器仍在）
            : messages?.map((i, index) => ({
                ...i.message,
                key: index,
                loading: i.status === 'loading',
                typing:
                  i.status === 'loading'
                    ? { step: 5, interval: 20, suffix: <>💗</> }
                    : undefined,
                messageRender:
                  i.message.role === 'assistant'
                    ? (content: any) => renderMarkdown(content)
                    : undefined,
              })) || []
        }
        roles={{
          assistant: {
            placement: 'start',
            avatar: (
              <img
                src="/images/ai-logo.png"
                alt="AI Logo"
                style={{ width: 32, height: 32, borderRadius: '50%' }}
              />
            ),
            typing: { step: 5, interval: 20, suffix: <>💗</> },
            footer: (content, info) => {
              const messageIndex = info.key as number;
              const isLastMessage = messageIndex === messages.length - 1;
              const isAssistant = messages[messageIndex]?.message.role === 'assistant';

              return (
                <>
                  {isLastMessage && isAssistant && (
                    <Tooltip title="重新生成回答">
                      <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => regenerateResponse()}
                        disabled={loading}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title="一键复制">
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(content)}
                    />
                  </Tooltip>
                </>
              );
            },
            loadingRender: () => (
              <Space>
                <Spin size="small" />
                {AGENT_PLACEHOLDER}
              </Space>
            ),
          },
          user: { placement: 'end' },
        }}
      />

      {/* 全局加载状态：覆盖在 Bubble.List 上 */}
      {loadingMessages && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)', // 可选：半透明遮罩
            zIndex: 9,
          }}
        >
          <Space>
            <Spin size="large" />
            <span>加载对话记录中...</span>
          </Space>
        </div>
      )}

      {/* 空状态内容：仅在无消息且非加载时显示 */}
      {!loadingMessages && (!messages || messages.length === 0) && (
        <>
          <Welcome
            variant="borderless"
            title="你好，我是未瓴AI助理，有什么可以帮助到你的吗?"
            description=""
            className={styles.chatWelcome} // ← 完全复用你的 css 样式
          />
          <div style={{ marginInline: 16, paddingBottom: 24 }}>
            <Prompts
              vertical
              items={MOCK_QUESTIONS.map((i) => ({ key: i, description: i }))}
              onItemClick={(info) => handleUserSubmit(info?.data?.description as string)}
              styles={{
                title: { fontSize: 14 },
              }}
            />
          </div>
        </>
      )}

      {/* “回到最新消息”按钮 */}
      {showScrollToBottom && (
        <div
          title="回到最新消息"
          onClick={() => scrollToBottom(true)}
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 16,
            zIndex: 10,
            width: 30,
            height: 30,
            borderRadius: '50%',
            backgroundColor: '#fff',
            border: '1px solid #d9d9d9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
            transition: 'all 0.2s ease-in-out',
            opacity: showScrollToBottom ? 1 : 0,
            visibility: showScrollToBottom ? 'visible' : 'hidden',
            pointerEvents: showScrollToBottom ? 'auto' : 'none',
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLElement).style.backgroundColor = '#f0f0f0';
            (e.target as HTMLElement).style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLElement).style.backgroundColor = '#fff';
            (e.target as HTMLElement).style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
          }}
        >
          <ArrowDownOutlined style={{ fontSize: 14 }} />
        </div>
      )}
    </div>
  );
  // const chatList = (
  //   <div
  //     ref={chatListRef} 
  //     className={styles.chatList}
  //     style={{ 
  //       height: '100%', 
  //       position: 'relative',
  //     }}
  //   >
  //     {loadingMessages ? (
  //       <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
  //         <Space>
  //           <Spin size="large" />
  //           <span>加载对话记录中...</span>
  //         </Space>
  //       </div>
  //     ) : messages?.length ? (<>
  //       <Bubble.List
  //         id = 'bubble-list-container'
  //         // autoScroll={true}
  //         // onScroll={(e) => {
  //         //   //console.log('滚动事件:', e);
  //         //   // 可用于自定义逻辑，比如检测是否在底部
  //         // }}
  //         style={{ 
  //           height: '100%', 
  //           paddingInline: 16,
  //           overflow: 'auto',
  //           //position: 'relative',
  //         }}
  //         items={messages?.map((i,index) => ({
  //           ...i.message,
  //           key: index,
  //           loading: i.status === 'loading' ? true: false,
  //           // classNames: {
  //           //   content: i.status === 'loading' ? styles.loadingMessage : '',
  //           // },
  //           typing: i.status === 'loading' ? { step: 5, interval: 20, suffix: <>💗</> } : undefined,
  //           // 智能内容渲染
  //           messageRender: i.message.role === 'assistant' ? (content: any) => (
  //             renderMarkdown(content)
  //           ) : undefined,
  //         }))}
  //         roles={{
  //           assistant: {
  //             placement: 'start',
  //             avatar: <img src="/images/ai-logo.png" alt="AI Logo" style={{ width: 32, height: 32, borderRadius: '50%' }} />,
  //             typing: { step: 5, interval: 20, suffix: <>💗</> },
  //             footer: (content, info) => {
  //               const messageIndex = info.key as number;
              
  //             // 判断是否是最后一条消息且是助理消息
  //             const isLastMessage = messageIndex === messages.length - 1;
  //             const isAssistant = messages[messageIndex]?.message.role === 'assistant';
  //             if(isLastMessage && isAssistant)
  //             {
  //               return (
  //                 <>
  //                   <Button 
  //                     type="text" 
  //                     size="small" 
  //                     icon={<ReloadOutlined />}
  //                     onClick={() => regenerateResponse()}
  //                     title="重新生成回答"
  //                     disabled={loading || !lastUserMessage}
  //                   />
  //                   <Button 
  //                     type="text" 
  //                     size="small" 
  //                     icon={<CopyOutlined />}
  //                     onClick={() => copyToClipboard(content)}
  //                     title="复制内容"
  //                   />
  //                 </>
  //               );
  //             }else{
  //               return (
  //                 <>
  //                   <Button 
  //                     type="text" 
  //                     size="small" 
  //                     icon={<CopyOutlined />}
  //                     onClick={() => copyToClipboard(content)}
  //                     title="复制内容"
  //                   />
  //                 </>
  //               );
  //             }},
  //             loadingRender: () => (
  //               <Space>
  //                 <Spin size="small" />
  //                 {AGENT_PLACEHOLDER}
  //               </Space>
  //             ),
  //           },
  //           user: { placement: 'end' },
  //         }}
  //       />
  //       {showScrollToBottom && (
  //       <div
  //         //icon={<ArrowDownOutlined />}
  //         title="回到最新消息"
  //         onClick={()=>scrollToBottom(true)}
  //         style={{
  //           position: 'absolute',
  //           left: '50%',
  //           transform: 'translateX(-50%)',
  //           bottom: 16,
  //           zIndex: 10,
  //           width: 30,
  //           height: 30,
  //           borderRadius: '50%',
  //           backgroundColor: '#fff',           // ← 白色背景
  //           border: '1px solid #d9d9d9',       // ← 灰色边框
  //           display: 'flex',
  //           alignItems: 'center',
  //           justifyContent: 'center',
  //           cursor: 'pointer',
  //           boxShadow: '0 2px 4px rgba(0,0,0,0.08)', // ← 轻柔阴影
  //           transition: 'all 0.2s ease-in-out',
  //         }}
  //         onMouseEnter={(e) => {
  //           (e.target as HTMLElement).style.backgroundColor = '#f0f0f0'; // 悬停时背景颜色变浅灰
  //           (e.target as HTMLElement).style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)'; // 加深阴影
  //         }}
  //         onMouseLeave={(e) => {
  //           (e.target as HTMLElement).style.backgroundColor = '#fff'; // 鼠标离开后恢复原状
  //           (e.target as HTMLElement).style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
  //         }}
  //       >
  //         <ArrowDownOutlined style={{ fontSize: 14 }} />
  //       </div>
  //     )}</>
  //     ) : (
  //       /** 没有消息时的 welcome */
  //       <>
  //         <Welcome
  //           variant="borderless"
  //           title="你好，我是未瓴AI助理，有什么可以帮助到你的吗?"
  //           description=""
  //           className={styles.chatWelcome}
  //         />

  //         <Prompts
  //           vertical
  //           //title="I can help："
  //           items={MOCK_QUESTIONS.map((i) => ({ key: i, description: i }))}
  //           onItemClick={(info) => handleUserSubmit(info?.data?.description as string)}
  //           style={{
  //             marginInline: 16,
  //           }}
  //           styles={{
  //             title: { fontSize: 14 },
  //           }}
  //         />
  //       </>
  //     )}
  //   </div>
  // );
  const sendHeader = (
    <Sender.Header
      title="Upload File"
      styles={{ content: { padding: 0 } }}
      open={attachmentsOpen}
      onOpenChange={setAttachmentsOpen}
      forceRender
    >
      <Attachments
        ref={attachmentsRef}
        beforeUpload={() => false}
        items={files}
        onChange={({ fileList }) => setFiles(fileList)}
        placeholder={(type) =>
          type === 'drop'
            ? { title: 'Drop file here' }
            : {
                icon: <CloudUploadOutlined />,
                title: 'Upload files',
                description: 'Click or drag files to this area to upload',
              }
        }
      />
    </Sender.Header>
  );
  const chatSender = (
    <div className={styles.chatSend} style={{margin: isFullscreen ? '0 15%':'0 0'}}>
      {/** 输入框 */}
      <Suggestion items={MOCK_SUGGESTIONS} onSelect={(itemVal) => setInputValue(`[${itemVal}]:`)}>
        {({ onTrigger, onKeyDown }) => (
          <Sender
            ref={inputRef}
            loading={loading}
            value={inputValue}
            autoSize={{minRows:3}}
            onChange={(v) => {
              onTrigger(v === '/');
              setInputValue(v);
            }}
            onSubmit={() => {
              handleUserSubmit(inputValue);
              setInputValue('');
            }}
            onCancel={() => {
              abortController.current?.abort();
            }}
            //allowSpeech
            placeholder="请将您遇到的问题告诉我，使用 Shift + Enter 换行"
            onKeyDown={onKeyDown}
            //header={sendHeader}
            // prefix={
            //   <Button
            //     type="text"
            //     icon={<PaperClipOutlined style={{ fontSize: 18 }} />}
            //     onClick={() => setAttachmentsOpen(!attachmentsOpen)}
            //   />
            // }
            actions={(_, info) => {
              const { SendButton, LoadingButton, SpeechButton } = info.components;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {/* <SpeechButton className={styles.speechButton} /> */}
                  {loading ? <LoadingButton type="default" /> : <SendButton type="primary" />}
                </div>
              );
            }}
          />
        )}
      </Suggestion>
    </div>
  );

   // Effects
  useEffect(() => {
    if (copilotOpen) loadSessions();
    setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
  }, [copilotOpen]);

  // useEffect(() => {
  //   if (curSession && copilotOpen) loadSessionMessages(curSession);
  // }, [curSession, copilotOpen]);

  return (
      <div 
        className={styles.layout}
        style={{ 
          width: copilotOpen ? (isFullscreen ? '100vw' : 550) : 0,
          height: isFullscreen? 'calc(100% - 106px)': '100%', // 占满 Drawer 的高度
          display: 'flex',          // 启用 Flexbox 布局
          //flexDirection: 'column',  // 子元素垂直排列
        }}
      >
        {isFullscreen && chatSider}

        <div 
          className={styles.copilotChat}
        >
            {/** 对话区 - header */}
            {chatHeader}

            {/** 对话区 - 消息列表 */}
            {chatList}

            {/** 对话区 - 输入框 */}
            {chatSender}

            {/** 免责声明 - 在最底部 */}
            <div style={{
              textAlign: 'center',
              fontSize: '12px',
              color: '#999',
              padding: '0px 16px 8px 0',
              margin: isFullscreen ? '0 15%':'0 0',
              flexShrink: 0 // 防止压缩
            }}>
              内容由 AI 生成，仅供参考，您据此所作判断及操作均由您自行承担责任。
            </div>
        </div>
      </div>
  );
};

export default Copilot;